<script setup>
import {useConfigurateurStore} from "../../stores/configurateur";
import {ref, computed} from 'vue';

const store = useConfigurateurStore();
const isCollapsed = ref(false);
const showModal = ref(false);
const selectedOptionForModal = ref(null);

// Variables pour la section KTM X-BOW R
const nb_tours_ktm = ref(0);
const modalType = ref('');

// Computed properties pour KTM X-BOW R
const hasKTMBOption = computed(() => {
    // Vérifier d'abord si ktmStage existe et n'est pas vide
    if (!store.ktmStage) {
        return false;
    }

    // Si ktmStage est un tableau, vérifier qu'il n'est pas vide
    if (Array.isArray(store.ktmStage) && store.ktmStage.length === 0) {
        return false;
    }

    // Vérifier si le stage sélectionné est déjà un stage KTM
    const isCurrentStageKTM = store.stage && (
        (store.stage.voiture_title && store.stage.voiture_title.includes('X-BOW')) ||
        (store.stage.short_ref && store.stage.short_ref.includes('XBOW')) ||
        (ktmData.value && store.stage.stage_id === ktmData.value.stage_id)
    );

    if (isCurrentStageKTM) {
        return false;
    }

    if (store.stage && store.stage.options) {
        const hasKTMB = store.stage.options.some(option => {
            return option.short_ref === 'KTMB';
        });

        return hasKTMB;
    }

    return false;
});

const ktmbOption = computed(() => {
    const option = store.stage?.options?.find(option => option.short_ref === 'KTMB') || null;
    return option;
});

const getKTMBPrice = computed(() => {
    const ktmData = Array.isArray(store.ktmStage) ? store.ktmStage[0] : store.ktmStage;
    return ktmData?.montant_ttc || 0;
});

const getKTMBMaxTours = computed(() => {
    // Utiliser les données du stage KTM du backend
    const ktmData = Array.isArray(store.ktmStage) ? store.ktmStage[0] : store.ktmStage;
    if (ktmData && ktmData.nb_tour_max) {
        return ktmData.nb_tour_max;
    }
    return 4; // Valeur par défaut
});

const getKTMBMinTours = computed(() => {
    // Utiliser les données du stage KTM du backend
    const ktmData = Array.isArray(store.ktmStage) ? store.ktmStage[0] : store.ktmStage;
    if (ktmData && ktmData.nb_tour_min) {
        return ktmData.nb_tour_min;
    }
    return 1; // Valeur par défaut
});

const getKTMBTotalPrice = computed(() => {
    if (nb_tours_ktm.value === 0) return 0;

    // Utiliser le prix calculé depuis les données du stage KTM
    const ktmData = Array.isArray(store.ktmStage) ? store.ktmStage[0] : store.ktmStage;
    if (ktmData && ktmData.tours) {
        const tourData = ktmData.tours.find(tour => tour.tour_number === nb_tours_ktm.value);
        if (tourData) {
            return tourData.price;
        }
    }

    // Fallback sur le calcul simple
    return getKTMBPrice.value * nb_tours_ktm.value;
});

// Computed pour extraire les données KTM
const ktmData = computed(() => {
    return Array.isArray(store.ktmStage) ? store.ktmStage[0] : store.ktmStage;
});

// Computed pour filtrer les options normales (excluant KTMB)
const normalOptions = computed(() => {
    if (!store.stage || !store.stage.options) return [];
    return store.stage.options.filter(option => option.short_ref !== 'KTMB');
});

// Fonctions pour la gestion KTM X-BOW R
const incrementKTM = () => {
    if (nb_tours_ktm.value < getKTMBMaxTours.value) {
        nb_tours_ktm.value++;
    }
};

const decrementKTM = () => {
    if (nb_tours_ktm.value > 0) {
        nb_tours_ktm.value--;
    }
};

const addKTM = () => {
    if (nb_tours_ktm.value === 0) {
        nb_tours_ktm.value = 1;
    }
    // Ajouter l'option KTM en utilisant les vraies données du stage
    addKTMToStore();
};

const removeKTM = () => {
    // Supprimer l'option KTM du store
    addKTMToStore(0);
    nb_tours_ktm.value = 0;
};

const addKTMToStore = (nbTours = nb_tours_ktm.value) => {
    const ktmData = Array.isArray(store.ktmStage) ? store.ktmStage[0] : store.ktmStage;
    if (ktmData && ktmbOption.value) {
        // Créer un objet de référence basé sur les données réelles du stage KTM
        let montant_ttc = ktmData.montant_ttc;
        if (nbTours > 0) {
            const tourData = ktmData.tours.find(tour => tour.tour_number == nbTours);
            if (tourData) {
                console.log(tourData);
                montant_ttc = tourData.price;
            }
        }

        const ktmReference = {
            id: ktmData.product_id,
            product_id: ktmData.product_id,
            stage_id: ktmData.stage_id,
            circuit_id: ktmData.circuit_id,
            voiture_id: ktmData.voiture_id,
            code: 'KTMB',
            montant_ttc: montant_ttc / nbTours,
            montant_ttc_par_tour: montant_ttc / nbTours,
            details: {
                nb_tour_min: ktmData.nb_tour_min,
                nb_tour_max: ktmData.nb_tour_max
            },
            designation: 'KTM X-BOW R',
            description: `Stage de pilotage KTM X-BOW R - ${ktmData.nb_tour_min} à ${ktmData.nb_tour_max} tours`
        };

        store.addOption(ktmReference, nbTours);
    }
};

const showKTMInfo = () => {
    modalType.value = 'ktm';
    const ktmData = Array.isArray(store.ktmStage) ? store.ktmStage[0] : store.ktmStage;
    // Créer un objet d'option temporaire pour la modal avec une structure compatible
    selectedOptionForModal.value = {
        designation: 'Baptême KTM X-BOW R',
        description: ktmData ?
            `Stage de pilotage KTM X-BOW R<br>
             <strong>Prix par tour:</strong> ${getKTMBPrice.value}€<br>
             <strong>Tours minimum:</strong> ${ktmData.nb_tour_min}<br>
             <strong>Tours maximum:</strong> ${ktmData.nb_tour_max}<br>
             <strong>Circuit:</strong> ${store.circuit?.designation || 'Circuit sélectionné'}`
            : 'Informations non disponibles',
        // Ajouter une propriété references vide pour éviter l'erreur
        references: [],
        isKTM: true // Flag pour identifier cette option spéciale
    };
    showModal.value = true;
};

const getReference = (option) => {
    if (option.references.length === 1) {
        return option.references[0];
    }
    const sortedReferences = [...option.references].sort((a, b) => {
        if (a.details.nb_tour && b.details.nb_tour) {
            return b.details.nb_tour - a.details.nb_tour;
        }
        if (a.details.nb_tour) return -1;
        if (b.details.nb_tour) return 1;

        if (a.details.nb_voiture && b.details.nb_voiture) {
            return b.details.nb_voiture - a.details.nb_voiture;
        }
        if (a.details.nb_voiture) return -1;
        if (b.details.nb_voiture) return 1;

        return 0;
    });
    let reference = null;
    for (const ref of sortedReferences) {
        if (ref.details.nb_tour && store.nb_tours >= ref.details.nb_tour) {
            reference = ref;
            break;
        } else if (ref.details.nb_voiture && store.nbVoitures >= ref.details.nb_voiture) {
            reference = ref;
            break;
        }
    }
    return reference || sortedReferences[0];
}

const getOptionImage = (option) => option?.vignette || null;

const withCircuitVisitorPrice = (reference) => {
    if (reference?.code !== 'ACC') return reference;

    return {
        ...reference,
        montant_ttc: store.circuit?.pass_accompagnateur_montant_ttc ?? reference.montant_ttc,
    };
};

const handleSelectOption = (event, reference) => {
    store.addOption(withCircuitVisitorPrice(reference), Number(event.target.value));
}

const handleSelectKTMOption = (event) => {
    const nbTours = parseInt(event.target.value);
    nb_tours_ktm.value = nbTours;

    // Utiliser les vraies données du stage KTM
    addKTMToStore(nbTours);
};

const handleCheckOption = (event, reference) => {
    store.addOption(withCircuitVisitorPrice(reference), event.target.checked ? 1 : 0);
}

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
}

const showOptionInfo = (option, event) => {
    selectedOptionForModal.value = option;
    showModal.value = true;
}

const closeModal = () => {
    showModal.value = false;
    selectedOptionForModal.value = null;
}

</script>

<template>
    <section id="step-option">
        <div class="card-step-group shadow-sm">
            <div class="step-title d-flex justify-content-between align-items-center">
                <div class="circle"
                     :class="{'active': store.getStepNumber('option') == store.step, 'completed': store.isCompleted(store.getStepNumber('option'))}">
                    {{ store.getStepNumber('option') }}
                </div>
                <span class="h5 large fw-bolder mb-2">{{ $t('Choisissez vos options') }}</span>
                <button class="btn btn-sm" type="button" @click="toggleCollapse">
                    <i class="bi" :class="isCollapsed ? 'bi-plus-circle' : 'bi-dash-circle'"></i>
                </button>
            </div>
            <div id="collapse-options"
                 class="collapse-content"
                 :class="{ 'collapsed': isCollapsed }">
                <div class="card card-step card-body">
                    <div class="row">
                        <template v-if="store.stage">

                            <!-- Option KTM X-BOW R avec la même mise en page que les autres options -->
                            <template v-if="hasKTMBOption">
                                <div class="col-6 col-xl-6 col-xxxl-4 p-2">
                                    <div
                                        class="card card-config card-option-config position-relative text-decoration-none"
                                        style="cursor: pointer;">

                                        <!-- Select pour choisir le nombre de tours KTM -->
                                        <div class="form-select-container">
                                            <select @change="handleSelectKTMOption($event)"
                                                    class="form-select w-auto form-check-input">
                                                <option value="0" selected>0</option>
                                                <option
                                                    v-for="i in Array.from({ length: getKTMBMaxTours - getKTMBMinTours + 1 }, (_, index) => getKTMBMinTours + index)"
                                                    :key="i" :value="i">{{ i }}
                                                </option>
                                            </select>
                                        </div>

                                        <div class="card-clickable-area">
                                            <i class="bi bi-info-circle-fill show-infos"
                                               @click.stop.prevent="showKTMInfo"
                                               title="Voir les détails de l'option KTM X-BOW R"></i>

                                            <img v-if="getOptionImage(ktmbOption)"
                                                :src="getOptionImage(ktmbOption)"
                                                alt="Baptême KTM X-BOW R"
                                                class="card-config-bg card-config-bg-cover">

                                            <div class="card-body">
                                                <div class="card-text">
                                                    <span class="fw-bold">
                                                        Baptême KTM X-BOW R
                                                    </span>
                                                </div>
                                                <div class="card-price">
                                                    <div class="price d-flex flex-row align-items-baseline">
                                                        <div class="fs-5 fw-bolder text-info">
                                                            {{ getKTMBPrice }}€
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <!-- Options normales (excluant KTMB) -->
                            <div v-for="(option, index) in normalOptions" :key="index"
                                 class="col-6 col-xl-6 col-xxxl-4 p-2">
                                <div class="card card-config card-option-config position-relative text-decoration-none"
                                     style="cursor: pointer;">

                                    <div v-if="option.qte_modifiable" class="form-select-container">
                                        <template v-if="getReference(option).code == 'ACC'">
                                            <select @change="handleSelectOption($event, getReference(option))"
                                                    class="form-select w-auto form-check-input">
                                                <template v-if="store.circuit">
                                                    <option
                                                        v-for="i in Array.from({ length: store.circuit.nb_max_pass_accompagnateur + 1 }, (_, index) => index)"
                                                        :key="i" :selected="i == 0">{{ i }}
                                                    </option>
                                                </template>
                                            </select>
                                        </template>
                                        <select v-else class="form-select w-auto form-check-input">
                                            <option selected>0</option>
                                            <option>1</option>
                                            <option>2</option>
                                            <option>3</option>
                                            <option>4</option>
                                        </select>
                                    </div>
                                    <div v-else class="form-switch">
                                        <template v-if="getReference(option).code == 'BPI' && store.optionRecoFree">
                                            <input class="form-check-input" type="checkbox" value=""
                                                   id="flexCheckDefault" disabled
                                                   checked/>
                                        </template>
                                        <template v-else>
                                            <input @change="handleCheckOption($event, getReference(option))"
                                                   class="form-check-input"
                                                   type="checkbox" value="" id="flexCheckDefault"/>
                                        </template>
                                    </div>
                                    <div class="card-clickable-area">
                                        <i class="bi bi-info-circle-fill show-infos"
                                           @click.stop.prevent="showOptionInfo(option)"
                                           title="Voir les détails de l'option"></i>

                                        <img v-if="getOptionImage(option)" :src="getOptionImage(option)"
                                             :alt="option.designation"
                                             class="card-config-bg card-config-bg-cover">

                                        <div class="card-body">
                                            <div class="card-text">
                                                <span class="fw-bold">
                                                    {{ option.designation }}
                                                    <template
                                                        v-if="getReference(option).code == 'ACC' && store.circuit">
                                                        <p>PASS INCLU(S) : {{
                                                                store.circuit.nb_compris_pass_accompagnateur
                                                            }}</p>
                                                    </template>
                                                    <!--<span v-else v-safe-html="option.description"></span>-->
                                                </span>
                                            </div>
                                            <div class="card-price">
                                                <div class="price d-flex flex-row align-items-baseline">
                                                    <div class="fs-5 fw-bolder text-info">
                                                        <template
                                                            v-if="getReference(option).code == 'ACC' && store.circuit">
                                                            {{ store.circuit.pass_accompagnateur_montant_ttc }}€
                                                        </template>
                                                        <template v-else>
                                                            <template
                                                                v-if="getReference(option).code == 'BPI' && store.optionRecoFree">
                                                                <span
                                                                    class="badge bg-danger text-white text-center fw-bolder">{{
                                                                        $t('Offert')
                                                                    }}</span>
                                                                <span class="text-decoration-line-through">{{
                                                                        getReference(option).montant_ttc
                                                                    }}€</span>
                                                            </template>
                                                            <template v-else>
                                                                {{ getReference(option).montant_ttc }}€
                                                            </template>
                                                        </template>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </template>
                    </div>
                </div>
            </div>

            <!-- Modal pour les informations de l'option -->
            <div v-if="showModal" class="step-modal-overlay" @click="closeModal">
                <div class="step-modal-content" @click.stop>
                    <div class="step-modal-header">
                        <h3 class="step-modal-title">
                            {{ selectedOptionForModal?.designation }}
                        </h3>
                        <button type="button" class="btn-close" @click="closeModal">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div class="step-modal-body">
                        <div v-if="selectedOptionForModal" class="card-option-infos">
                            <div class="card-text">
                                <!-- Gestion spéciale pour les options KTM -->
                                <template v-if="selectedOptionForModal.isKTM">
                                    <div v-safe-html="selectedOptionForModal.description"></div>
                                </template>
                                <!-- Options normales -->
                                <template v-else>
                                    <template
                                        v-if="getReference(selectedOptionForModal).code == 'ACC' && store.circuit">
                                        <p><strong>PASS INCLUS :</strong>
                                            {{ store.circuit.nb_compris_pass_accompagnateur }}
                                        </p>
                                        <p><strong>Prix par accompagnement supplémentaire :</strong>
                                            {{ store.circuit.pass_accompagnateur_montant_ttc }}€</p>
                                        <p><strong>Maximum d'accompagnements :</strong>
                                            {{ store.circuit.nb_max_pass_accompagnateur }}</p>
                                    </template>
                                    <div v-else v-safe-html="selectedOptionForModal.description"></div>

                                    <div class="mt-3">
                                        <p><strong>Prix :</strong></p>
                                        <template
                                            v-if="getReference(selectedOptionForModal).code == 'ACC' && store.circuit">
                                            <span class="fs-5 fw-bolder text-info">{{
                                                    store.circuit.pass_accompagnateur_montant_ttc
                                                }}€</span>
                                        </template>
                                        <template v-else>
                                            <template
                                                v-if="getReference(selectedOptionForModal).code == 'BPI' && store.optionRecoFree">
                                                <span class="badge bg-danger text-white text-center fw-bolder me-2">{{
                                                        $t('Offert')
                                                    }}</span>
                                                <span class="text-decoration-line-through">{{
                                                        getReference(selectedOptionForModal).montant_ttc
                                                    }}€</span>
                                            </template>
                                            <template v-else>
                                                <span class="fs-5 fw-bolder text-info">{{
                                                        getReference(selectedOptionForModal).montant_ttc
                                                    }}€</span>
                                            </template>
                                        </template>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.collapse-content {
    overflow: hidden;
    transition: max-height 0.3s ease-out;
    max-height: 2000px; /* Valeur suffisamment grande pour le contenu */
}

.collapse-content.collapsed {
    max-height: 0;
}
</style>
